import { DeleteOutline, EditOutlined, FolderOutlined, GridViewOutlined } from '@mui/icons-material'
import { Box, alpha, styled, useTheme } from '@mui/material'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { ConfigurationCard } from 'src/generated-types/configure-positions'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { getConfigurePalette } from './configurePalette'
import { positionLabel } from './positionLabels'
import DrawingThumb from './DrawingThumb'

interface ConfigurationCardItemProps {
  card: ConfigurationCard
  thumbnailUrl?: string
  highlighted: boolean
  onMouseEnter: () => void
  onMouseLeave: () => void
  onEdit: (card: ConfigurationCard) => void
  onRemove: (card: ConfigurationCard) => void
}

const ConfigurationCardItem = React.forwardRef<HTMLDivElement, ConfigurationCardItemProps>(
  ({ card, thumbnailUrl, highlighted, onMouseEnter, onMouseLeave, onEdit, onRemove }, ref) => {
    const theme = useTheme()
    const { t } = useTranslation(['position', 'common'])
    const isGrouped = !!card.groupName

    return (
      <CardRoot
        ref={ref}
        grouped={isGrouped}
        highlighted={highlighted}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <CardHeader>
          <ThumbBox>
            <DrawingThumb url={thumbnailUrl} alt={card.drawingNumber} />
          </ThumbBox>
          <CardTitleColumn>
            <DrawingNumber>{card.drawingNumber}</DrawingNumber>
            {isGrouped ? (
              <GroupBadge>
                <FolderOutlined sx={{ fontSize: 14 }} />
                {card.groupName}
              </GroupBadge>
            ) : (
              <UngroupedLabel>
                <GridViewOutlined sx={{ fontSize: 14 }} />
                {t('configure-not-grouped', { ns: 'position' })}
              </UngroupedLabel>
            )}
          </CardTitleColumn>
          <CardActions>
            <GridActionButton
              toolTipTitle={t('edit', { ns: 'common' })}
              aria-label={t('edit', { ns: 'common' })}
              buttonIcon={
                <EditOutlined fontSize="small" htmlColor={getThemeColor(theme, MUIThemeColors.primaryMain)} />
              }
              onClick={() => onEdit(card)}
              width="32px"
              height="32px"
              border={0}
              shadowWeight={null}
            />
            <GridActionButton
              toolTipTitle={t('remove-drawing', { ns: 'position' })}
              aria-label={t('remove-drawing', { ns: 'position' })}
              buttonIcon={<DeleteOutline fontSize="small" htmlColor={getThemeColor(theme, MUIThemeColors.errorDark)} />}
              onClick={() => onRemove(card)}
              width="32px"
              height="32px"
              border={0}
              shadowWeight={null}
            />
          </CardActions>
        </CardHeader>
        <PositionStrip grouped={isGrouped}>
          {card.positions.map(position => (
            <PositionTag key={position.id} grouped={isGrouped}>
              {positionLabel(position)}
            </PositionTag>
          ))}
        </PositionStrip>
      </CardRoot>
    )
  },
)

export default ConfigurationCardItem

const forwardGroupedProps = (prop: PropertyKey) => prop !== 'grouped' && prop !== 'highlighted'

const CardRoot = styled(Box, { shouldForwardProp: forwardGroupedProps })<{ grouped: boolean; highlighted: boolean }>(
  ({ theme, grouped, highlighted }) => {
    const palette = getConfigurePalette(theme)
    return {
      backgroundColor: grouped ? palette.paper : palette.surface,
      border: grouped ? `1.5px solid ${palette.primary}` : `1px solid ${palette.line}`,
      outline: highlighted ? `2px solid ${palette.primary}` : 'none',
      outlineOffset: 1,
      boxShadow: grouped ? `4px 4px 0 -1.5px ${palette.paper}, 4px 4px 0 0 ${palette.pale}` : 'none',
      borderRadius: 6,
      padding: theme.spacing(1.5),
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
    }
  },
)

const CardHeader = styled(Box)(({ theme }) => ({ display: 'flex', alignItems: 'center', gap: theme.spacing(1.5) }))

const ThumbBox = styled(Box)(({ theme }) => ({
  width: 120,
  aspectRatio: '16 / 10',
  flex: 'none',
  border: `1px solid ${getThemeColor(theme, MUIThemeColors.secondaryLight)}`,
  borderRadius: 4,
  overflow: 'hidden',
}))

const CardTitleColumn = styled(Box)(({ theme }) => ({
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.75),
}))

const DrawingNumber = styled(Box)(({ theme }) => ({
  fontWeight: 600,
  fontSize: 15,
  color: getThemeColor(theme, MUIThemeColors.primaryDark),
}))

const GroupBadge = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  backgroundColor: getConfigurePalette(theme).selectedSurface,
  color: getThemeColor(theme, MUIThemeColors.primaryDark),
  fontWeight: 600,
  fontSize: 12.5,
  padding: theme.spacing(0.5, 1),
  borderRadius: 12,
  maxWidth: '100%',
}))

const UngroupedLabel = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  color: getThemeColor(theme, MUIThemeColors.secondaryMain),
  fontWeight: 600,
  fontSize: 12,
}))

const CardActions = styled(Box)({ marginLeft: 'auto', display: 'flex' })

// Grouped cards render their positions as one joined, bordered strip; ungrouped ones as loose tags.
const PositionStrip = styled(Box, { shouldForwardProp: forwardGroupedProps })<{ grouped: boolean }>(
  ({ theme, grouped }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: grouped ? 0 : theme.spacing(0.5),
    alignSelf: 'flex-start',
    border: grouped ? `1px solid ${getThemeColor(theme, MUIThemeColors.primaryMain)}` : 'none',
    borderRadius: 4,
    overflow: 'hidden',
  }),
)

const PositionTag = styled(Box, { shouldForwardProp: forwardGroupedProps })<{ grouped: boolean }>(
  ({ theme, grouped }) => {
    const palette = getConfigurePalette(theme)
    return {
      fontSize: 11,
      fontWeight: 600,
      padding: theme.spacing(0.25, 0.75),
      color: grouped ? palette.primaryDark : palette.primary,
      backgroundColor: grouped ? palette.selectedSurface : palette.paper,
      border: grouped ? 'none' : `1px solid ${palette.line}`,
      borderRadius: grouped ? 0 : 4,
      ...(grouped && {
        borderLeft: `1px solid ${alpha(palette.primary, 0.25)}`,
        '&:first-of-type': { borderLeft: 'none' },
      }),
    }
  },
)
